// Libraries
import moment from 'moment-timezone';
import React from 'react';

// Components
import { CardBlock, CardText } from 'Components/flowChart/components/NodePreview/components/styled';

// Types
import { IAudiences, IMetadata } from 'Components/flowChart/components/NodePreview/components/NodeScheduled/types';
import { INodePreviewProps } from 'Components/flowChart/components/NodePreview/types';

// Constants
import {
	FREQUENCY_LABEL,
	SEGMENT_TYPE_LABEL,
} from 'Components/flowChart/components/NodePreview/components/NodeScheduled/constants';
import { FORMAT_FULL_DATE } from 'Components/flowChart/components/NodePreview/constants';

// Utils
import { countFilterSegments } from 'Components/flowChart/utils';

const PATH = 'components/components/flowChart/components/Card/components/NodeScheduled/index.tsx';

export interface INodeScheduled extends INodePreviewProps<IMetadata> {}

const NodeScheduled: React.FC<INodeScheduled> = (props) => {
	try {
		const {
			node: { metadata },
			timezone,
		} = props;
		const { triggerType, startTime, endTime, frequency, repeatInterval, audiences } = metadata;

		const triggerInterval =
			frequency === 'once' ? FREQUENCY_LABEL[frequency] : `Every ${repeatInterval} ${FREQUENCY_LABEL[frequency]}(s)`;

		const renderSchedule = () => {
			if (triggerType === 'after_activate') {
				return (
					<CardBlock>
						<CardText>Start right after the journey activated</CardText>
					</CardBlock>
				);
			}

			if (triggerType === 'specific_date') {
				const startDate = startTime ? moment(startTime).tz(timezone).format(FORMAT_FULL_DATE) : null;
				const endDate = endTime ? moment(endTime).tz(timezone).format(FORMAT_FULL_DATE) : 'None';

				return (
					<CardBlock>
						{startDate && (
							<>
								<CardText>Start date</CardText>
								<CardText icon="icon-antsomi-event">{startDate}</CardText>
							</>
						)}
						{frequency !== 'once' && endDate && (
							<>
								<CardText>Close date</CardText>
								<CardText icon="icon-antsomi-event">{endDate}</CardText>
							</>
						)}
					</CardBlock>
				);
			}

			return null;
		};

		const renderFilters = () => {
			const { audienceSegmentType, excludedAudiences, includedAudiences } = audiences as IAudiences;
			const numIncludedSegments = countFilterSegments(includedAudiences?.filters);
			const numIncludedAudiences = includedAudiences?.specificAudienceIds?.length;
			const numExcludedSegments = countFilterSegments(excludedAudiences?.filters);
			const numExcludedAudiences = excludedAudiences?.specificAudienceIds?.length;
			const audienceType = SEGMENT_TYPE_LABEL[audienceSegmentType];

			if (
				[numIncludedSegments, numIncludedSegments, numExcludedSegments, numExcludedAudiences].every((item) => !item)
			) {
				return null;
			}

			return (
				<CardBlock>
					{numIncludedSegments > 0 && (
						<CardText>{`Included ${numIncludedSegments} ${audienceType} segment(s)`}</CardText>
					)}
					{numIncludedAudiences > 0 && (
						<CardText>{`Included ${numIncludedAudiences} specific ${audienceType}(s)`}</CardText>
					)}
					{numExcludedSegments > 0 && (
						<CardText>{`Excluded ${numExcludedSegments} ${audienceType} segment(s)`}</CardText>
					)}
					{numExcludedAudiences > 0 && (
						<CardText>{`Excluded ${numExcludedAudiences} specific ${audienceType}(s)`}</CardText>
					)}
				</CardBlock>
			);
		};

		return (
			<>
				<CardBlock>
					<CardText>{`Trigger journey: ${triggerInterval}`}</CardText>
				</CardBlock>
				{renderSchedule()}
				{renderFilters()}
			</>
		);
	} catch (e) {
		props.onError?.(e, {
			component: PATH,
			action: 'render -> NodeScheduled',
			args: { props },
		});

		return null;
	}
};

export default NodeScheduled;
